/**
 * Instructor reviews list route: /instructors/$instructorId/reviews
 *
 * Sole host of the SSR loader since the parallel `_authenticated/instructor-reviews`
 * family was removed; see the studio counterpart for the reasoning.
 */
import { createFileRoute } from '@tanstack/react-router';
import type { QueryClient } from '@tanstack/react-query';
import { ListPageTemplatePending } from '@/components/v2/page-template-pending';
import { authenticatedGetShared } from '@/lib/api/authenticated-shared';
import { cacheFirst } from '@/lib/queries/cache-first';
import type { InstructorReviewsResponseV2 } from '@/types/discovery';

export const PER_PAGE = 20;

async function loadInstructorReviewsRoute(queryClient: QueryClient, instructorId: string) {
  const reviewsKey = ['instructor-reviews', instructorId] as const;
  const refresh = () =>
    queryClient.ensureInfiniteQueryData({
      queryKey: reviewsKey,
      // Dual transport (#1290 pattern): same endpoint + envelope unwrap as the
      // component's getInstructorReviews queryFn, so the seeded first page is
      // shape-identical under the shared key (hasMore gates getNextPageParam).
      queryFn: () =>
        authenticatedGetShared<InstructorReviewsResponseV2>(
          `/v2/instructors/${instructorId}/reviews?limit=${PER_PAGE}`
        ),
      initialPageParam: 0,
    });
  // Cache-first gate: re-entry (back-navigation or an intent preload with the
  // first page already cached) resolves instantly while ensureInfiniteQueryData
  // refreshes stale entries in the background; a cold cache keeps the awaited
  // fetch the SSR stream renders against.
  const { cached, stream } = cacheFirst(queryClient, reviewsKey, refresh);
  return cached !== undefined ? Promise.resolve(cached) : stream();
}

export const Route = createFileRoute('/_public/instructors/$instructorId/reviews')({
  // Template-first pending: header+list shape while the lazy chunk downloads
  // (matches the list page the route becomes), instead of the full-page mascot.
  pendingComponent: ListPageTemplatePending,
  loader: ({ context, params }) =>
    loadInstructorReviewsRoute(context.queryClient, params.instructorId),
  headers: () => ({ 'Cache-Control': 'private, no-store' }),
});
